import Box from "@@/elements/Box";
import Container from "@@/elements/Container";
import Icon from "@@/elements/Icons/Icon";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Close,
  MenuIcon,
  SearchIcon,
  Utilities,
} from "@@/elements/Icons/icons";
import Typography from "@@/elements/Typography";
import {
  ScreenSize,
  useIsBreakpointAndAbove,
  useIsBreakpointOrBelow,
} from "@@/hooks/device/use-breakpoint";
import useScrollDirection from "@@/hooks/use-scroll-direction";
import { useScrollLock } from "@@/hooks/use-scroll-lock";
import useScrollPosition from "@@/hooks/use-scroll-position";
import cn from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { TopMenuMobile } from "../TopMenu";
import { MenuAriaLabels, MenuProps } from "../headerProps";
import Accordion from "./Accordion";
import DesktopNav from "./DesktopNav";
import Drawer from "./Drawer";
import MainMenuSearch from "./Search";
import styles from "./index.module.scss";

const DEFAULT_ARIA_LABELS: MenuAriaLabels = {
  navLabel: "Main",
  openMenuLabel: "Open menu",
  closeMenuLabel: "Close menu",
  searchLabel: "Search",
  goBackLabel: "Go back",
  submenuLabel: "Submenu for {name}",
};

type Props = MenuProps;

const MainMenu = ({
  logoUrl,
  logoAltText,
  logoLink,
  logoSize = "large",
  navigationItems,
  company,
  mobileOnlyMenu,
  searchData,
  ariaLabels,
}: Props) => {
  const labels = { ...DEFAULT_ARIA_LABELS, ...ariaLabels };

  const [drawer, setDrawer] = useState<boolean>(false);
  const [drawer1, setDrawer1] = useState<boolean>(false);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [currentDrawerMenu, setCurrentDrawerMenu] =
    useState<number | undefined>(undefined);

  const drawerRef = useRef<HTMLDivElement>();
  const drawerRef1 = useRef<HTMLDivElement>();
  const searchRef = useRef<HTMLDivElement>();

  const scrollDirection = useScrollDirection();
  const scrollPosition = useScrollPosition();
  const { lock, unlock } = useScrollLock();
  const isLarge = useIsBreakpointAndAbove(ScreenSize.LARGE);
  const isSmall = useIsBreakpointOrBelow(ScreenSize.SMALL);

  // Scroll-hide/sticky behavior
  useEffect(() => {
    const menuElement = document.querySelector("#main-menu");
    menuElement?.parentElement?.classList.add("main-menu");
  }, []);

  useEffect(() => {
    const menuElement = document.querySelector("#main-menu");
    const searchMobileNotOpen = isLarge || !searchOpen;
    const menuNotOpen = !(drawer || drawer1);
    const hideMenu = isSmall ? scrollPosition > 100 : scrollPosition > 150;

    if (hideMenu && scrollDirection !== "down") {
      menuElement?.parentElement?.classList.add("main-menu-sticky");
    } else if (scrollPosition === 0) {
      menuElement?.parentElement?.classList.remove("main-menu-sticky");
    }

    if (scrollDirection === "down" && searchMobileNotOpen && menuNotOpen && hideMenu) {
      menuElement?.parentElement?.classList.add("main-menu-hide");
    }
    if (scrollDirection === "up" && searchMobileNotOpen && menuNotOpen) {
      menuElement?.parentElement?.classList.remove("main-menu-hide");
    }
  }, [scrollDirection, scrollPosition, isLarge, isSmall, searchOpen, drawer, drawer1]);

  // Close drawer on outside click
  const closeDrawer = useCallback((event: any) => {
    if (
      !drawerRef.current?.contains(event.target) &&
      !drawerRef1.current?.contains(event.target) &&
      !searchRef.current?.contains(event.target) &&
      (drawer || drawer1)
    ) {
      setDrawer(false);
      setDrawer1(false);
      setCurrentDrawerMenu(undefined);
      unlock();
    }
  }, [drawer, drawer1, unlock]);

  useEffect(() => {
    document.addEventListener("mousedown", closeDrawer);
    return () => document.removeEventListener("mousedown", closeDrawer);
  }, [closeDrawer]);

  const onOpenSearch = (open: boolean, event: any = null) => {
    if (
      event &&
      (drawerRef.current?.contains(event.target) ||
        drawerRef1.current?.contains(event.target))
    ) {
      return;
    }
    setSearchOpen(open);
    if (open) {
      if (!isLarge) {
        lock();
      }
      document.getElementById("main_menu_search_input")?.focus();
    } else {
      document.getElementById("main_menu_search_input")?.blur();
      unlock();
    }
  };

  const openDrawer = (): void => {
    setDrawer(true);
    lock();
  };

  const openDrawerMenu = (index: number): void => {
    setCurrentDrawerMenu(index);
    setDrawer1(true);
  };

  const logoClassNames = cn(styles.logo, {
    [styles.logo__small]: logoSize === "small",
  });

  const linkClassNames = cn(styles.menu_items, {
    [styles[`menu_items_${company}`]]: company,
  });

  const [activeSubMenuItem, setActiveSubMenuItem] = useState<number | null>(null);

  useEffect(() => {
    if (!drawer1) setActiveSubMenuItem(null);
  }, [drawer1]);

  const getDrawerSubMenu = () => {
    if (
      currentDrawerMenu !== undefined &&
      currentDrawerMenu < navigationItems.length
    ) {
      return (
        <ul className={styles.accordion_list}>
          {navigationItems[currentDrawerMenu].children.map((element, index) => (
            <Box
              component="li"
              width="full"
              key={index}
            >
              {element.children.length > 0 ? (
                <Box width="full" mb={0.5}>
                  <Accordion
                    company={company}
                    menuChoice={element}
                    open={index === activeSubMenuItem}
                    onClick={() => {
                      if (activeSubMenuItem === index) {
                        setActiveSubMenuItem(null);
                      } else setActiveSubMenuItem(index);
                    }}
                  />
                </Box>
              ) : (
                <a
                  href={element.link}
                  key={index}
                  target={element.target}
                  className={linkClassNames}
                  data-menu-type="menu drawer"
                  data-menu-level={element.depth}
                  data-menu-text={element.name}
                  data-menu-path={element.link}
                >
                  <Typography variant="submenu1">{element.name}</Typography>
                  {element.target && <Utilities className={styles.ext_link} />}
                </a>
              )}
            </Box>
          ))}
        </ul>
      );
    }

    if (
      currentDrawerMenu !== undefined &&
      currentDrawerMenu >= navigationItems.length
    ) {
      return (
        <ul className={styles.market_container}>
          {mobileOnlyMenu.languageSelector.header && (
            <Typography px={7.5} pb={3} pt={2} variant="submenu1">
              {mobileOnlyMenu.languageSelector.header}
            </Typography>
          )}
          {mobileOnlyMenu.languageSelector.items &&
            mobileOnlyMenu.languageSelector.items.map((item, index) => (
              <li
                key={index}
                className={
                  item.active
                    ? cn(styles.active_item, {
                      [styles[`active_item_${company}`]]: company,
                    })
                    : cn(styles.market_item, {
                      [styles[`market_item_${company}`]]: company,
                    })
                }
              >
                <a
                  href={item.link.href}
                  target={item.link.target}
                  title={item.link.title}
                  data-menu-type="menu utilities"
                  data-menu-level="2"
                  data-menu-text={item.link.text}
                  data-menu-path={item.link.href}
                >
                  <Typography asElement="span" variant="menu">
                    {item.link.text}
                  </Typography>
                  {item.link.target && (
                    <Utilities width={14} className={styles.ext_link} />
                  )}
                </a>
              </li>
            ))}
        </ul>
      );
    }

    return <></>;
  };

  return (
    <>
      {drawer && <Box className={styles.overlay} />}
      <Container id="main-menu" size="full" pageGutter={0} positionRoot>
        <div className={styles.main_container}>
          <div className={styles.content_width}>
            <a className={logoClassNames} href={logoLink.href}>
              <img src={logoUrl} alt={logoAltText} />
            </a>

            <DesktopNav
              navigationItems={navigationItems}
              company={company}
              ariaLabels={ariaLabels}
              searchOpen={searchOpen}
            />

            <div className={styles.flex}>
              {mobileOnlyMenu.mobileItems &&
                mobileOnlyMenu.mobileItems.map((mobileItem, index) => (
                  <a
                    key={index}
                    className={cn(
                      styles.mobile_link,
                      "mobile_link__icon",
                      `icon-${mobileItem.icon}`,
                    )}
                    href={mobileItem.link.href}
                    title={mobileItem.link.title}
                    data-menu-type="menu drawer"
                    data-menu-level="1"
                    data-menu-text={mobileItem.link.text}
                    data-menu-path={mobileItem.link.href}
                  >
                    <Icon
                      name={mobileItem.icon}
                      title={mobileItem.iconAltText}
                      height={20}
                      width={20}
                    />
                  </a>
                ))}

              <button
                aria-label={labels.searchLabel}
                aria-expanded={searchOpen}
                aria-controls="search-wrapper"
                type="button"
                id="search_button"
                className={styles.search}
                onClick={() => onOpenSearch(!searchOpen)}
                data-menu-type="menu drawer"
                data-menu-level="1"
                data-menu-text="search"
                data-menu-path=""
              >
                <SearchIcon />
              </button>

              <button
                type="button"
                aria-label={labels.openMenuLabel}
                aria-expanded={drawer}
                onClick={openDrawer}
                className={styles.hamburger}
              >
                <MenuIcon />
              </button>

              <div ref={searchRef}>
                <MainMenuSearch
                  open={searchOpen}
                  setOpen={onOpenSearch}
                  searchData={searchData}
                />
              </div>
            </div>
          </div>
        </div>
      </Container>

      {/* TABLET AND MOBILE */}
      <nav aria-label={labels.navLabel}>
      <Drawer ref={drawerRef} open={drawer} className={styles.drawer}>
        <div>
          <div className={styles.icon_container}>
            <button
              aria-label={labels.goBackLabel}
              type="button"
              onClick={() => setCurrentDrawerMenu(undefined)}
            />
            <button
              type="button"
              aria-label={labels.closeMenuLabel}
              onClick={() => {
                setDrawer(false);
                unlock();
                setCurrentDrawerMenu(undefined);
              }}
            >
              {currentDrawerMenu === undefined ? <Close /> : <Box pt={6} />}
            </button>
          </div>

          <ul className={styles.mobile_container}>
            {navigationItems &&
              navigationItems.map((menuItem, index) => (
                <Box component="li" width="full" key={index}>
                  <button
                    aria-label={menuItem.name}
                    type="button"
                    aria-expanded={drawer1 && currentDrawerMenu === index}
                    key={index}
                    className={cn(
                      styles.menu_items,
                      styles.menu_items_submenu1,
                    )}
                    onClick={() => openDrawerMenu(index)}
                    data-menu-type="menu drawer"
                    data-menu-level={menuItem.depth}
                    data-menu-text={menuItem.name}
                    data-menu-path=""
                  >
                    <Typography key={menuItem.name} variant="menu">
                      {menuItem.name}
                    </Typography>
                    <ChevronRight />
                  </button>
                </Box>
              ))}
          </ul>
        </div>

        <div>
          <TopMenuMobile
            onClick={() => {
              setDrawer1(true);
              setCurrentDrawerMenu(navigationItems.length);
            }}
            languageSelector={mobileOnlyMenu.languageSelector}
            company={company}
            items={mobileOnlyMenu.items}
            mobileButton={mobileOnlyMenu.mobileButton}
          />
        </div>
      </Drawer>

      <Drawer ref={drawerRef1} open={drawer1} className={styles.drawer1}>
        <div>
          <div className={styles.icon_container}>
            <button
              aria-label={labels.goBackLabel}
              type="button"
              onClick={() => {
                setCurrentDrawerMenu(undefined);
                setDrawer1(false);
              }}
            >
              {currentDrawerMenu !== undefined && <ChevronLeft />}
            </button>
            <button
              aria-label={labels.closeMenuLabel}
              type="button"
              onClick={() => {
                setDrawer(false);
                setDrawer1(false);
                unlock();
                setCurrentDrawerMenu(undefined);
              }}
            >
              <Close />
            </button>
          </div>
          {getDrawerSubMenu()}
        </div>
      </Drawer>
      </nav>
    </>
  );
};

export default MainMenu;
